import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useAdminAuth } from "./AdminAuthContext";
import { useToast } from "./ToastProvider";
import { ShieldCheck, ShieldX, KeyRound, Trash2 } from "lucide-react";

/**
 * MFA management card, mounted on Settings.
 * States:
 *   loading -> not_enrolled -> enrolling -> verifying -> done
 *   loading -> enrolled (with Disable button)
 */
export default function AdminMFACard() {
  const { session, apiFetch } = useAdminAuth();
  const toast = useToast();
  const [status, setStatus] = useState({ loading: true, enrolled: false, factors: [], aal: "aal1" });
  const [phase, setPhase] = useState("idle"); // idle | enrolling | verifying
  const [factorId, setFactorId] = useState(null);
  const [qrSvg, setQrSvg] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await apiFetch(`/admin/mfa/status`);
      setStatus({ loading: false, ...data });
    } catch (e) {
      console.error("MFA status load failed:", e?.response?.data?.detail || e.message);
      setStatus({ loading: false, enrolled: false, factors: [], aal: "aal1" });
    }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const beginEnroll = async () => {
    setError(""); setBusy(true);
    try {
      const { data, error: err } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: `Damage Culture Admin - ${navigator.platform || "device"}`,
        issuer: "Damage Culture",
      });
      if (err) throw err;
      setFactorId(data.id);
      setQrSvg(data.totp.qr_code);
      setSecret(data.totp.secret);
      setPhase("enrolling");
    } catch (e) {
      setError(e.message || "Failed to start enrollment");
    } finally { setBusy(false); }
  };

  const verifyEnroll = async () => {
    if (!factorId || code.length !== 6) return;
    setError(""); setBusy(true);
    try {
      const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId });
      if (cErr) throw cErr;
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: code.replace(/\s/g, ""),
      });
      if (vErr) throw vErr;
      await supabase.auth.refreshSession();
      toast("Two-factor authentication enabled", "ok");
      setPhase("idle");
      setFactorId(null); setQrSvg(""); setSecret(""); setCode("");
      await load();
    } catch (e) {
      setError(e.message || "Wrong code. Try again.");
    } finally { setBusy(false); }
  };

  const cancelEnroll = async () => {
    // If we started an enrollment but didn't verify, unenroll the stub factor.
    if (factorId) {
      try {
        await supabase.auth.mfa.unenroll({ factorId });
      } catch (e) {
        console.warn("could not clean up unverified MFA factor:", e?.message || e);
      }
    }
    setPhase("idle"); setFactorId(null); setQrSvg(""); setSecret(""); setCode(""); setError("");
  };

  const disable = async () => {
    if (!window.confirm("Turn off two-factor auth? Your account will only be protected by password.")) return;
    setBusy(true); setError("");
    try {
      // Use supabase client-side which requires the current aal2 session.
      for (const f of status.factors) {
        const { error: err } = await supabase.auth.mfa.unenroll({ factorId: f.id });
        if (err) throw err;
      }
      await supabase.auth.refreshSession();
      toast("Two-factor authentication disabled", "ok");
      await load();
    } catch (e) {
      setError(e.message || "Failed to disable");
    } finally { setBusy(false); }
  };

  if (status.loading) {
    return <div style={cardStyle}><span style={mutedStyle}>Loading two-factor status...</span></div>;
  }

  return (
    <div style={cardStyle} data-testid="admin-mfa-card">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        {status.enrolled ? (
          <ShieldCheck size={18} style={{ color: "#10B981" }} />
        ) : (
          <ShieldX size={18} style={{ color: "#EF4444" }} />
        )}
        <h3 style={titleStyle}>Two-factor authentication</h3>
        <span style={{ ...pillStyle, borderColor: status.enrolled ? "#10B981" : "#EF4444", color: status.enrolled ? "#10B981" : "#EF4444" }}>
          {status.enrolled ? "enabled" : "not enabled"}
        </span>
      </div>
      <p style={{ ...mutedStyle, marginBottom: 14 }}>
        Adds a 6-digit code from Google Authenticator (or any TOTP app) on every admin sign-in. Codes rotate every 30 seconds.
      </p>

      {/* Enrolled state */}
      {status.enrolled && phase === "idle" && (
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#8B8B93", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>
            Active factor
          </div>
          {status.factors.map((f) => (
            <div key={f.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: "#0F0E0D", borderRadius: 8, marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 13 }}>{f.friendly_name || "TOTP factor"}</div>
                <div style={{ fontSize: 11, color: "#8B8B93", fontFamily: "'JetBrains Mono', monospace" }}>
                  Enrolled {f.created_at ? new Date(f.created_at).toISOString().slice(0, 10) : ""}
                </div>
              </div>
            </div>
          ))}
          <button className="adm-btn adm-btn-danger" onClick={disable} disabled={busy} data-testid="mfa-disable-btn" style={{ marginTop: 6 }}>
            <Trash2 size={13} /> Disable two-factor auth
          </button>
          {status.aal !== "aal2" && (
            <p style={{ ...mutedStyle, marginTop: 8 }}>
              Sign out and back in with your 2FA code to disable this factor.
            </p>
          )}
        </div>
      )}

      {/* Not enrolled - offer setup */}
      {!status.enrolled && phase === "idle" && (
        <button className="adm-btn" onClick={beginEnroll} disabled={busy} data-testid="mfa-enable-btn">
          <KeyRound size={13} /> Set up Google Authenticator
        </button>
      )}

      {/* Enrolling: show QR + secret + verify field */}
      {phase === "enrolling" && (
        <div style={{ background: "#0F0E0D", borderRadius: 10, padding: 16, marginTop: 8 }}>
          <p style={{ ...mutedStyle, marginBottom: 12 }}>
            1. Open Google Authenticator (or 1Password, Authy, etc.) on your phone.<br />
            2. Scan the QR code below, or enter the manual key.<br />
            3. Type the 6-digit code it shows.
          </p>
          <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
            {qrSvg && (
              <div style={{ background: "#FFF", padding: 12, borderRadius: 8 }}>
                {/* Rendered as an <img> data-URI: an image can never execute
                    script, unlike injecting the SVG markup into the DOM. */}
                <img
                  src={qrSvg.trim().startsWith("data:")
                    ? qrSvg
                    : `data:image/svg+xml;utf8,${encodeURIComponent(qrSvg)}`}
                  alt="Two-factor QR code"
                  width={180}
                  height={180}
                  data-testid="mfa-qr"
                />
              </div>
            )}
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "#8B8B93", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>
                Manual key
              </div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, wordBreak: "break-all", padding: "8px 10px", background: "#17171A", borderRadius: 6, marginBottom: 14 }}>
                {secret}
              </div>

              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "#8B8B93", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>
                6-digit code
              </div>
              <input
                className="adm-input"
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                data-testid="mfa-enroll-code"
                style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.3em", fontSize: 20, textAlign: "center", marginBottom: 12 }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="adm-btn"
                  onClick={verifyEnroll}
                  disabled={busy || code.length !== 6}
                  data-testid="mfa-verify-btn"
                >
                  {busy ? "Verifying..." : "Verify and enable"}
                </button>
                <button className="adm-btn adm-btn-ghost" onClick={cancelEnroll} disabled={busy}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <p style={{ color: "#EF4444", marginTop: 10, fontSize: 13 }} data-testid="mfa-error">{error}</p>
      )}
    </div>
  );
}

const cardStyle = {
  background: "#17171A",
  border: "1px solid #26262C",
  borderRadius: 12,
  padding: 18,
};
const titleStyle = {
  margin: 0,
  fontFamily: "'Anton', 'Arial Black', Impact, sans-serif",
  fontSize: 22,
  letterSpacing: "0.02em",
  textTransform: "uppercase",
  color: "#ECECEE",
  flex: 1,
};
const mutedStyle = {
  color: "#8B8B93",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  lineHeight: 1.6,
};
const pillStyle = {
  display: "inline-block",
  padding: "3px 10px",
  border: "1px solid",
  borderRadius: 999,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 10.5,
  letterSpacing: "0.06em",
  textTransform: "lowercase",
};
